"use client";

import Image from "@/components/shared/image";
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import {
  getHomepageMotionEnvironment,
  shouldUseHeavyHomepageMotion,
} from "@/components/LandingPage/mobile-motion-policy";

type MetaFlipBadgeProps = {
  className?: string;
  /** How long each face stays on screen before rolling (ms). Default 4000. */
  intervalMs?: number;
  /** Duration of one roll (ms). Default 900. */
  rollMs?: number;
};

/**
 * Pill badge that rolls UPWARD around the X-axis between two faces,
 * like an odometer ticking up. Every `intervalMs` the rotor advances
 * by another -180°, swapping Meta ↔ Claude.
 *
 *   Face A — "Meta Official Business Partner"        (rotateX 0°)
 *   Face B — "Claude responds to your DMs"           (rotateX 180°)
 *
 * Pure React state + CSS transform — no GSAP, no extra animation
 * libraries.
 */
export function MetaFlipBadge({
  className,
  intervalMs = 4000,
  rollMs = 900,
}: MetaFlipBadgeProps) {
  // Every tick subtracts 180° so the top edge rolls toward the viewer.
  // Using a monotonically decreasing value (rather than `% 360`) keeps
  // the rotation moving in the same direction forever, never reversing.
  const [angle, setAngle] = useState(0);
  const [useMotion, setUseMotion] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;

    setUseMotion(shouldUseHeavyHomepageMotion(getHomepageMotionEnvironment()));
  }, []);

  useEffect(() => {
    if (!useMotion) return;

    let intervalId: ReturnType<typeof setInterval> | null = null;

    const tick = () => {
      setAngle((prev) => prev - 180);
    };

    const stop = () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const start = () => {
      if (intervalId !== null || document.hidden) return;
      intervalId = setInterval(tick, intervalMs);
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        stop();
      } else {
        start();
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    start();

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      stop();
    };
  }, [intervalMs, useMotion]);

  const rotorStyle = useMotion
    ? {
      transformStyle: "preserve-3d" as const,
      transform: `rotateX(${angle}deg)`,
      transition: `transform ${rollMs}ms cubic-bezier(0.65, 0, 0.35, 1)`,
      willChange: "transform",
    }
    : {
      transform: "none",
      transition: "none",
      willChange: "auto",
    };

  return (
    <div
      className={cn("inline-block select-none", className)}
      style={{ perspective: "900px", perspectiveOrigin: "50% 50%" }}
      aria-label="Meta Official Business Partner — Claude responds to your DMs"
    >
      <div
        className="relative h-[46px] w-[min(16rem,calc(100vw-2rem))] sm:h-[52px] sm:w-[20rem]"
        style={rotorStyle}
      >
        <Face rotateX={0} z={1}>
          <MetaFace />
        </Face>
        {useMotion ? (
          <Face rotateX={-180} z={-1}>
            <ClaudeFace />
          </Face>
        ) : null}
      </div>
    </div>
  );
}

const PILL_CLASS = cn(
  "meta-flip-badge-face absolute inset-0 flex items-center justify-center overflow-hidden rounded-full isolate",
  "bg-[#f5f5f3] pl-2 pr-3 sm:pl-2.5 sm:pr-5",
);

function Face({
  rotateX,
  z,
  children,
}: {
  rotateX: number;
  z: number;
  children: ReactNode;
}) {
  return (
    <div
      className={PILL_CLASS}
      style={{
        backfaceVisibility: "hidden",
        WebkitBackfaceVisibility: "hidden",
        transform: `rotateX(${rotateX}deg) translateZ(${z}px)`,
      }}
    >
      {children}
    </div>
  );
}

function MetaFace() {
  return (
    <div className="relative z-[2] flex min-w-0 items-center gap-1.5 sm:gap-3">
      <span className="flex h-8 w-[60px] shrink-0 items-center justify-center sm:h-12 sm:w-[88px]">
        <Image
          src="/meta_partner.webp"
          alt="Meta Business Partner"
          width={88}
          height={46}
          className="h-full w-auto object-contain"
          priority
          sizes="(max-width: 639px) 60px, 88px"
          srcSet="/meta_partner_88w.webp 88w, /meta_partner.webp 176w"
        />
      </span>
      <span className="h-5 w-px shrink-0 bg-neutral-900/25 sm:h-6" aria-hidden />
      <span className="min-w-0 whitespace-nowrap text-[11.5px] font-semibold tracking-tight text-neutral-900 sm:text-[14px]">
        Meta Official Business Partner
      </span>
    </div>
  );
}

function ClaudeFace() {
  return (
    <div className="relative z-[2] flex min-w-0 items-center gap-2 sm:gap-3">
      <span className="flex size-6 shrink-0 items-center justify-center sm:size-8">
        <Image
          src="/claude_logo_32w.webp"
          alt="Claude"
          width={32}
          height={32}
          className="size-full rounded-[7px] object-contain"
          srcSet="/claude_logo_32w.webp 32w, /claude_logo_64w.webp 64w"
          sizes="32px"
          style={{ maxWidth: '32px', maxHeight: '32px' }}
        />
      </span>
      <span className="h-5 w-px shrink-0 bg-neutral-900/25 sm:h-6" aria-hidden />
      <span className="min-w-0 whitespace-nowrap text-[12px] font-semibold tracking-tight text-neutral-900 sm:text-[14px]">
        Claude responds to your DMs
      </span>
    </div>
  );
}

export default MetaFlipBadge;
